import { TabItemType } from '@/core/data/config/uiConfig.interface';
import type {
  BaseAttendanceDto,
  BaseGambitDto,
  GambitFormData,
  GambitRecurrenceOptions,
  SparkGambitOptions,
} from '@/core/data/gambit/gambit.interface';
import type { GambitInvitationType, LiveGambitCheckIn } from '@/core/data/gambit/gambit.type';
import type { GeoCoords } from '@/core/data/location/location.interface';
import { gambits } from '@/core/gambits';
import { GambitEndpoint } from '@/core/network/api/constants/api.url.constant';
import { baseGambitFields, detailGambitFields } from '@/core/network/api/constants/gambit.api_fields';
import { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse, GetGambitTableFilters, GetTableParams } from '@/core/network/http/httpClient.interface';
import { useFilterStore } from '@/stores/filter.store';
import { useTableStore } from '@/stores/table.store';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamFilterSortGambits } from '@/views/composables/constants/main/filter.constants';
import type { GambitSearchResultDto } from '@/views/composables/models/components/GambitSearchResultDto';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import type { AxiosResponse } from 'axios';

export interface RespondGambitDto {
  id: string;
  invitationType: 'challenge' | 'request';
}

export interface LiveGambitDto {
  id: string;
  title: string;
  start: string;
  end: string;
  location: { id: string; name: string; coordinates: GeoCoords } | null;
  checkIn: LiveGambitCheckIn;
}

export interface OutgoingInvitationDto {
  id: string;
  invitationType: 'challenge' | 'request';
  end: string;
}

export class GambitApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  private getGambitsParams(id: GamListId): GetGambitTableFilters {
    const store = useTableStore<BaseGambitDto>(id)();
    const gambitFilters = useFilterStore(id)();

    const activeTab = gambitFilters.getActiveTab();

    const filters = gambitFilters.getFilters() as GetGambitTableFilters;
    const onlyOneFutureRecurrence = filters?.onlyOneFutureRecurrence === true;

    return {
      ...store.tableParams,
      // ...store.getLocationParam(),
      myGambits: activeTab === TabItemType.MINE,
      following: activeTab === TabItemType.FOLLOWING,
      fields: baseGambitFields.join(','),
      sortBy: GamFilterSortGambits.GAMBIT_TIME,
      onlyOneFutureRecurrence,
    };
  }

  async addGambit(data: GambitFormData): Promise<AxiosResponse<GamResponse<BaseGambitDto>>> {
    return await this.httpClient.post(GambitEndpoint.GAMBITS, {
      data,
    });
  }

  async createFirestoreDoc(id: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.put(GambitEndpoint.GAMBIT_FIRESTORE, {
      urlParams: { id },
    });
  }

  async updateGambit(
    id: string,
    data: GambitFormData,
    options?: GambitRecurrenceOptions,
  ): Promise<AxiosResponse<GamResponse<BaseGambitDto>>> {
    let url;
    if (options?.recurrences) {
      url = `${GambitEndpoint.GAMBIT_DETAILS}?recurrences=true`;
    } else {
      url = GambitEndpoint.GAMBIT_DETAILS;
    }

    return await this.httpClient.put(url, {
      urlParams: { id },
      data,
    });
  }

  async updateChallenge(
    id: string,
    data: Partial<GambitFormData>,
  ): Promise<AxiosResponse<GamResponse<{ message: string; gambitId?: string }>>> {
    return await this.httpClient.post(GambitEndpoint.GAMBIT_INVITATION, {
      urlParams: { id },
      data,
    });
  }

  async deleteGambit(
    id: string,
    options?: GambitRecurrenceOptions,
  ): Promise<AxiosResponse<GamResponse<BaseGambitDto>>> {
    let url;
    if (options?.recurrences) {
      url = `${GambitEndpoint.GAMBIT_DETAILS}?recurrences=true`;
    } else {
      url = GambitEndpoint.GAMBIT_DETAILS;
    }

    return await this.httpClient.delete(url, {
      urlParams: { id },
    });
  }

  async joinSpark(data: SparkGambitOptions): Promise<AxiosResponse<GamResponse<BaseGambitDto>>> {
    const url = `${GambitEndpoint.GAMBITS}/spark`;

    return await this.httpClient.post(url, {
      data,
    });
  }

  async createAttendance(
    id: string,
    options?: { recurrences?: boolean },
  ): Promise<AxiosResponse<GamResponse<BaseAttendanceDto>>> {
    let url;
    if (options?.recurrences) {
      url = `${GambitEndpoint.GAMBIT_DETAILS}/attendance?recurrences=true`;
    } else {
      url = `${GambitEndpoint.GAMBIT_DETAILS}/attendance`;
    }

    return await this.httpClient.post(url, {
      urlParams: { id },
    });
  }

  async updateAttendance(
    gambitId: string,
    chessSetCount: number,
    status?: string,
  ): Promise<AxiosResponse<GamResponse<BaseAttendanceDto>>> {
    const url = `${GambitEndpoint.GAMBIT_DETAILS}/attendance`;

    return await this.httpClient.put(url, {
      urlParams: { id: gambitId },
      data: { chessSetCount, status },
    });
  }

  async deleteAttendance(
    id: string,
    options?: { recurrences?: boolean },
  ): Promise<AxiosResponse<GamResponse<BaseAttendanceDto>>> {
    let url;
    if (options?.recurrences) {
      url = `${GambitEndpoint.GAMBIT_DETAILS}/attendance?recurrences=true`;
    } else {
      url = `${GambitEndpoint.GAMBIT_DETAILS}/attendance`;
    }

    return await this.httpClient.delete(url, {
      urlParams: { id },
    });
  }

  async findGambitResults(
    locationIds: string[],
    start: string | Date,
    end: string | Date,
  ): Promise<AxiosResponse<GamResponse<GambitSearchResultDto[]>>> {
    return this.httpClient.get<GambitSearchResultDto[], GetGambitTableFilters>(GambitEndpoint.GAMBITS, {
      config: {
        params: {
          locationIds,
          start: start,
          end: end,
          fields: detailGambitFields.join(','),
          sortBy: GamFilterSortGambits.GAMBIT_TIME,
          onlyOneFutureRecurrence: true,
        },
      },
    });
  }

  async findMyGambits(
    start: string | Date,
    end: string | Date,
  ): Promise<AxiosResponse<GamResponse<GambitSearchResultDto[]>>> {
    return this.httpClient.get<GambitSearchResultDto[], GetGambitTableFilters>(GambitEndpoint.GAMBITS, {
      config: {
        params: {
          start: start,
          end: end,
          fields: 'id,start,end,title,gambitCategory,timezone,isAttending,locationSummary',
          onlyOneFutureRecurrence: false,
          myGambits: true,
        },
      },
    });
  }

  async getGambits(listId: GamListId, location: GeoCoords): Promise<AxiosResponse<GamResponse<BaseGambitDto[]>>> {
    return this.httpClient.get<BaseGambitDto[], GetGambitTableFilters>(GambitEndpoint.GAMBITS, {
      config: {
        params: {
          ...this.getGambitsParams(listId),
          ...{
            latitude: location.lat,
            longitude: location.lng,
            distanceLte: gambits.configService.getGisRadius().default,
          },
        },
      },
    });
  }

  async getGambit(id: string, location?: GeoCoords): Promise<AxiosResponse<GamResponse<GambitDetailDto>>> {
    const params: GetGambitTableFilters = {
      fields: detailGambitFields.join(','),
    };

    if (location) {
      params.latitude = location.lat;
      params.longitude = location.lng;
    }

    return this.httpClient.get<GambitDetailDto, GetTableParams>(GambitEndpoint.GAMBIT_DETAILS, {
      urlParams: { id },
      config: { params },
    });
  }

  async createInvitations(
    gambitId: string,
    userIds: string[],
    type: GambitInvitationType,
  ): Promise<AxiosResponse<GamResponse<any>>> {
    const url = `${GambitEndpoint.GAMBIT_DETAILS}/invitations`;
    return await this.httpClient.post(url, {
      urlParams: { id: gambitId },
      data: {
        userIds,
        type,
      },
    });
  }

  async declineInvitation(gambitId: string): Promise<AxiosResponse<GamResponse<any>>> {
    const url = `${GambitEndpoint.GAMBIT_DETAILS}/declineInvitation`;
    return await this.httpClient.post(url, {
      urlParams: { id: gambitId },
    });
  }

  async acceptChallenge(gambitId: string): Promise<AxiosResponse<GamResponse<any>>> {
    const url = `${GambitEndpoint.GAMBIT_DETAILS}/acceptInvitation`;
    return await this.httpClient.post(url, {
      urlParams: { id: gambitId },
    });
  }

  async withdrawChallenge(gambitId: string): Promise<AxiosResponse<GamResponse<any>>> {
    const url = `${GambitEndpoint.GAMBIT_DETAILS}/withdrawInvitation`;
    return await this.httpClient.post(url, {
      urlParams: { id: gambitId },
    });
  }

  async getRespondInvitations(): Promise<AxiosResponse<GamResponse<RespondGambitDto[]>>> {
    return await this.httpClient.get(GambitEndpoint.GAMBIT_RESPOND, {});
  }

  async getLiveGambits(): Promise<AxiosResponse<GamResponse<LiveGambitDto[]>>> {
    return await this.httpClient.get(GambitEndpoint.GAMBIT_LIVE, {});
  }

  async dismissRecentGambit(gambitId: string): Promise<AxiosResponse<GamResponse<boolean>>> {
    return await this.httpClient.post(GambitEndpoint.GAMBIT_DISMISS_RECENT, {
      urlParams: { id: gambitId },
    });
  }

  async getOutgoingInvitations(userId: string): Promise<AxiosResponse<GamResponse<OutgoingInvitationDto[]>>> {
    return await this.httpClient.get(GambitEndpoint.GAMBIT_USER_GATHERING_INVITATIONS, {
      urlParams: { userId },
    });
  }

  async cancelGambitInvitation(gambitId: string): Promise<AxiosResponse<GamResponse<any>>> {
    const url = `${GambitEndpoint.GAMBIT_DETAILS}/cancelInvitation`;
    return await this.httpClient.post(url, {
      urlParams: { id: gambitId },
    });
  }
}
